import type { ProjectSettings } from '../types/database';

/**
 * Converte duração em formato string (ex: "3d", "8h", "2w", "1m") para horas totais.
 * Respeita as configurações do projeto: horas/dia e dias úteis por semana.
 *
 * Unidades suportadas:
 *   h = horas
 *   d = dias (× hoursPerDay)
 *   w = semanas (× workDaysPerWeek × hoursPerDay)
 *   m = meses (× 4 × workDaysPerWeek × hoursPerDay)
 * Sem unidade → dias.
 *
 * IMPORTANTE: os tokens d/w/m/h são FORMATO CANÔNICO DE ARMAZENAMENTO. A coluna
 * `tasks.duration` guarda a string com o sufixo, então localizar as letras (por
 * exemplo 's' de semana em espanhol) corromperia todas as durações já gravadas.
 * Traduza o RÓTULO exibido, nunca o token. O teste em __tests__/duration.test.ts
 * trava esse contrato.
 */
export function estimateHoursFromDuration(
  duration: string | null | undefined,
  hoursPerDay = 8,
  workDaysPerWeek = 5
): number {
  if (!duration) return hoursPerDay;
  const match = String(duration).trim().match(/^([\d.]+)\s*([dwhm]?)$/i);
  if (!match) {
    // Antes isto devolvia o default calado. Um token desconhecido é bug de dado
    // (ou de localização indevida do sufixo), não uma tarefa de um dia.
    console.warn('[duration] formato não reconhecido, usando 1 dia:', duration);
    return hoursPerDay;
  }
  const value = parseFloat(match[1]);
  if (!Number.isFinite(value) || value <= 0) return hoursPerDay;
  const unit = (match[2] || 'd').toLowerCase();
  if (unit === 'h') return value;
  if (unit === 'd') return value * hoursPerDay;
  if (unit === 'w') return value * workDaysPerWeek * hoursPerDay;
  if (unit === 'm') return value * 4 * workDaysPerWeek * hoursPerDay;
  return value * hoursPerDay;
}

/**
 * Quantidade de semanas (arredondada para cima) entre duas datas.
 * Mínimo 1 — projetos curtos não deveriam produzir zero semanas.
 */
export function weeksBetween(startDate: string | Date, endDate: string | Date): number {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 1;
  const days = (end - start) / 86_400_000;
  return Math.max(1, Math.ceil(days / 7));
}

/**
 * Total de horas úteis por semana que o calendário do projeto comporta.
 * Usado como cap em alocações, hint inline em inputs de horas/semana e
 * base do cálculo de capacidade nominal.
 *
 * Default: 5 dias × 8h = 40h/sem (calendário padrão Mon-Fri).
 */
export function getProjectWorkWeekHours(settings: ProjectSettings | null | undefined): number {
  const workDaysPerWeek = settings?.workDays?.length ?? 5;
  const hoursPerDay = settings?.hoursPerDay ?? 8;
  return Math.max(1, workDaysPerWeek * hoursPerDay);
}
